import React, { useMemo, useState } from "react";
import { Button, Dropdown, Form, Spinner } from "react-bootstrap";
import { apiGenericUpdate, genericGetOne } from "../generic/crud";
import { ENTITY_ID_SALES_CUSTOMER } from "../generic/Constants";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";

const PIN_REGEX = /^[0-9]{6}$/;
const DEFAULT_LIST_SIZE = 5;

/**
 * "Ship To" picker for the e-Way bill: choose one of the customer's saved
 * shipping addresses (Bill To / Ship To) or keep the billing address.
 * Also allows adding a new shipping address to the customer on the spot.
 */
export function ShipToAddressSelect({ customer, value, onChange, onCustomerRefresh, readOnly = false }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [adding, setAdding] = useState(false);
    const [newAddress, setNewAddress] = useState("");
    const [newPinCode, setNewPinCode] = useState("");
    const [addError, setAddError] = useState("");
    const [saving, setSaving] = useState(false);

    const addresses = useMemo(() => customer?.shippingAddresses || [], [customer]);
    const selected = addresses.find((a) => a.id === value) || null;

    // Search primarily by pin code (prefix first, then substring); address text as a bonus.
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return addresses.slice(0, DEFAULT_LIST_SIZE);
        const pinPrefix = addresses.filter((a) => String(a.pinCode || "").startsWith(q));
        const pinSub = addresses.filter((a) => !pinPrefix.includes(a) && String(a.pinCode || "").includes(q));
        const addrSub = addresses.filter((a) => !pinPrefix.includes(a) && !pinSub.includes(a)
            && String(a.address || "").toLowerCase().includes(q));
        return [...pinPrefix, ...pinSub, ...addrSub];
    }, [addresses, search]);

    const closeMenu = () => { setOpen(false); setSearch(""); setAdding(false); setAddError(""); };
    const pick = (id) => { onChange(id); closeMenu(); };

    // Append the new address to the customer record, then re-read the customer to
    // pick up the id the client backend stamped on the new row, and auto-select it.
    const handleAddNew = async () => {
        const address = newAddress.trim();
        const pinCode = newPinCode.trim();
        if (!address) { setAddError("Address is required"); return; }
        if (!PIN_REGEX.test(pinCode)) { setAddError("Pin code must be exactly 6 digits"); return; }
        setAddError("");
        setSaving(true);
        try {
            const customerId = customer.id;
            const { data: fresh } = await genericGetOne(ENTITY_ID_SALES_CUSTOMER, customerId);
            const updatedList = [...(fresh?.shippingAddresses || []), { address, pinCode }];
            await apiGenericUpdate(ENTITY_ID_SALES_CUSTOMER, { ...fresh, shippingAddresses: updatedList }, customerId);
            const { data: updated } = await genericGetOne(ENTITY_ID_SALES_CUSTOMER, customerId);
            if (onCustomerRefresh) onCustomerRefresh(updated);
            const matches = (updated?.shippingAddresses || [])
                .filter((a) => a.address === address && String(a.pinCode) === pinCode);
            const added = matches[matches.length - 1];
            if (added?.id) onChange(added.id);
            showToast("Shipping address added", TOAST_SUCCESS);
            setNewAddress("");
            setNewPinCode("");
            closeMenu();
        } catch (error) {
            showToast("Could not add shipping address: " + (error.response?.data || error.message), TOAST_FAILURE);
        } finally {
            setSaving(false);
        }
    };

    const billingLine = customer
        ? [customer.address, customer.pinCode].filter(Boolean).join(" — ") || "No billing address on record"
        : "Loading customer details…";
    const selectionLabel = selected ? "Shipping address" : "Same as billing address";
    const selectionDetail = selected
        ? [selected.address, selected.pinCode].filter(Boolean).join(" — ")
        : billingLine;

    return (
        <div className="mt-2 px-3 py-2" style={{ borderRadius: '8px', border: '1px solid #d6d3ff', backgroundColor: '#f6f5ff' }}>
            <div className="d-flex align-items-center mb-1">
                <i className="bi bi-geo-alt me-2" style={{ color: '#635BFF', fontSize: '0.85rem' }}></i>
                <span className="fw-semibold" style={{ color: '#635BFF', fontSize: '0.72rem', letterSpacing: '0.4px' }}>SHIP TO</span>
            </div>
            {readOnly ? (
                <div className="small">
                    <span className="fw-semibold">{selectionLabel}</span>
                    <span className="d-block text-muted">{selectionDetail}</span>
                </div>
            ) : (
                <Dropdown show={open} onToggle={(next) => (next ? setOpen(true) : closeMenu())} autoClose="outside">
                    <Dropdown.Toggle
                        variant="light"
                        className="w-100 text-start d-flex align-items-center justify-content-between"
                        style={{ border: '1px solid #ced4da', backgroundColor: '#ffffff', borderRadius: '6px' }}
                        disabled={!customer}
                    >
                        <span className="text-truncate me-2">
                            <span className="fw-semibold small">{selectionLabel}</span>
                            <span className="d-block text-muted text-truncate" style={{ fontSize: '0.72rem' }}>{selectionDetail}</span>
                        </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 shadow-sm" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        {!adding && (
                            <>
                                <div className="px-3 py-1">
                                    <Form.Control
                                        size="sm"
                                        autoFocus
                                        placeholder="Search by pin code…"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <Dropdown.Item as="button" type="button" active={!selected} onClick={() => pick("")}>
                                    <span className="fw-semibold small">Same as billing address</span>
                                    <span className={`d-block text-truncate ${!selected ? '' : 'text-muted'}`} style={{ fontSize: '0.72rem' }}>{billingLine}</span>
                                </Dropdown.Item>
                                {filtered.map((a, idx) => (
                                    <Dropdown.Item as="button" type="button" key={a.id || idx} active={!!a.id && a.id === value} onClick={() => pick(a.id)}>
                                        <span className="d-block text-truncate small">{a.address} — {a.pinCode}</span>
                                    </Dropdown.Item>
                                ))}
                                {search.trim() && filtered.length === 0 && (
                                    <div className="px-3 py-1 small text-muted">No matching shipping address</div>
                                )}
                                <Dropdown.Divider />
                                <Dropdown.Item as="button" type="button" onClick={() => { setAdding(true); setAddError(""); }}>
                                    <span className="small" style={{ color: '#635BFF' }}><i className="bi bi-plus-circle me-2"></i>Add new address</span>
                                </Dropdown.Item>
                            </>
                        )}
                        {adding && (
                            <div className="px-3 py-2">
                                <div className="fw-semibold small mb-2" style={{ color: '#635BFF' }}>New shipping address</div>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    size="sm"
                                    className="mb-2"
                                    placeholder="Address *"
                                    autoFocus
                                    value={newAddress}
                                    onChange={(e) => setNewAddress(e.target.value)}
                                    disabled={saving}
                                />
                                <Form.Control
                                    size="sm"
                                    className="mb-2"
                                    placeholder="Pin Code (6 digits) *"
                                    maxLength={6}
                                    value={newPinCode}
                                    onChange={(e) => setNewPinCode(e.target.value.replace(/\D/g, ""))}
                                    disabled={saving}
                                />
                                {addError && <div className="small text-danger mb-2">{addError}</div>}
                                <div className="d-flex gap-2">
                                    <Button size="sm" variant="primary" onClick={handleAddNew} disabled={saving}>
                                        {saving && <Spinner animation="border" size="sm" className="me-1" />}
                                        Save
                                    </Button>
                                    <Button size="sm" variant="light" onClick={() => { setAdding(false); setAddError(""); }} disabled={saving}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            )}
        </div>
    );
}
